import { HttpClient } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OidcClientService } from '@upmc-healthplan-npm/oidc-client';
import { Subject, Observable, Subscriber, of, BehaviorSubject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';

import { POLOAuthConstants } from '../constants/pol-oauth.constants';
import { ELaunchDarklyFlag, Microservices } from '../constants/pol.enums';
import { IPOLUserProfileResponse, IPOLUserResponse } from '../models/state.model';
import { ENVIRONMENT } from 'src/app/tokens';
import { IConfig } from 'src/app/config.interface';
import { LaunchDarklyService } from '../layout/launch-darkly/launch-darkly.service';
import { SignOutOptions } from '../guards/auth.guard';

declare const window: Window;

@Injectable({
  providedIn: 'root'
})
export class POLAuthService {
  unsubscribe = new Subject();
  hasUserAgreementData$ = new BehaviorSubject({ hasUserAgreementData: false, useAgreementVaule: false });
  userAgreementLdFlag$ = new BehaviorSubject<boolean>(false);
  userAgreementFlag: boolean;

  constructor(
    private auth: OidcClientService,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    @Inject(ENVIRONMENT) private environment: IConfig['environment'],
    private launchDarkly: LaunchDarklyService,
  ) {
    // initial value
    this.launchDarkly.flags$.subscribe((res) => {
      this.userAgreementFlag = res[ELaunchDarklyFlag.SignUp];
      this.userAgreementLdFlag$.next(this.userAgreementFlag);
    });
  }

  signIn(): Observable<boolean> {
    return new Observable((observer) => {
      this.auth
        .getUserComputed()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((user) => {
          if (!user || this.auth.isExpired()) {
            // grabs hash value from sso and strips out the authentication token
            const hash = window.location.hash; // salesforce sso
            const idToken = this.route.snapshot.queryParamMap.get('id_token'); // producer portal sso
            if (hash && hash.includes('id_token=')) {
              const authTokenIndex = hash.indexOf('id_token=');
              const authToken = hash.substring(authTokenIndex + 9); // strip everything but the token
              this.createSignIn(authToken, observer);
            } else if (idToken) {
              this.createSignIn(idToken, observer);
            } else {
              observer.next(false);
            }
          } else {
            observer.next(true);
          }
        });
    });
  }

  createSignIn(token: string, observer: Subscriber<boolean>): void {
    this.auth
      .createSignInFromIdToken(token)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((state) => {
        const uri = `/#state=${state}&id_token=${token}`;
        this.completeSignIn(uri, observer);
      });
  }

  completeSignIn(uri: string, observer: Subscriber<boolean>): void {
    this.auth.completeSignIn(uri).subscribe((signedInUser) => {
      if (signedInUser) {
        observer.next(false);
      } else {
        observer.next(false);
      }
    });
  }

  getPOLUserProfile(): Observable<IPOLUserResponse> {
    const apiUrl = `${Microservices.ProviderOnline}${this.userAgreementFlag ? '/v2' : ''}/login`;
    return this.http.post(apiUrl, {}).pipe(
      map((response: IPOLUserResponse) => {
        if (response?.userProfile?.providerType) {
          response.userProfile.providerType = response.userProfile.providerType.toLowerCase();
        }

        return response;
      }),
      catchError(() => {
        this.router.navigateByUrl('login-error');
        return of(null);
      })
    );
  }

  refreshTokenUrl(): string {
    const redirectUri = POLOAuthConstants.redirectUri;
    const client_id = POLOAuthConstants.client_id;
    const user = this.auth.getUser();
    const env = this.environment.env === 'prod' ? '' : this.environment.env;

    let url = `https://upmchp${env}.b2clogin.com/upmchp${env}.onmicrosoft.com/B2C_1A_LOCALPROVIDERNGSIGNIN${env}`;
    url += `?p=B2C_1A_LOCALPROVIDERNGSIGNIN${env}`;
    url += `&client_id=${client_id}`;
    url += `&redirect_uri=${redirectUri}`;
    url += `&response_type=id_token`;
    url += `&scope=openid`;
    url += `&nonce=defaultNonce`;
    url += `&client_assertion_type=urn:ietf:params:oauth:client-assertion-type:jwt-bearer`;
    url += `&client_assertion=${user.id_token}`;
    // url += `&response_mode=query`;
    return url;
  }

  saveUseAgreementDate(documentUrl: string) {
    const apiUrl = `${Microservices.ProviderOnline}/SaveUseAgreementDate`;
    return this.http.post(apiUrl, { documentUrl }).pipe(
      map((response) => response),
      catchError(() => {
        this.router.navigateByUrl('login-error');
        return of(null);
      })
    );
  }

  signOutRedirect(options?: SignOutOptions, authOptions = {}): void {
    this.auth.userManager.removeUser().then(() => {
      sessionStorage.clear();
      if (options?.intendedRoute) {
        localStorage.setItem('intended-route', options.intendedRoute);
      }

      this.auth[options?.signOut ? 'signOut' : 'signIn'](authOptions);
    });
  }
}
